/*
 * Copyright 2026 Sony Corporation
 */

import { Engine } from '@babylonjs/core/Engines/engine';
import { useEffect, useRef } from 'react';

// Installs registerView: the viewport and the preview are both engine views.
import '@babylonjs/core/Engines/AbstractEngine/abstractEngine.views';
// Registers every shader the editor draws with; see the file for why.
import '../../scene/babylon-shaders';

import { createRigManager, type RigManager } from '../../scene/rig-manager';
import { createAmbientOcclusion } from '../../scene/ambient-occlusion';
import { createSceneEditor, type SceneEditor } from '../../scene/scene-editor';
import { createSceneSwitcher, type SceneSwitcher } from '../../scene/scene-switcher';
import type { Transform } from '../../scene/types';

export interface BabylonContext {
  editor: SceneEditor;
  switcher: SceneSwitcher;
  /** Absent when `withRigs` is false, which is how the Scene page renders scenery only. */
  rigs?: RigManager;
}

export interface BabylonCanvasProps {
  className?: string;
  /** Called once the editor exists; the caller chooses the first scene and may return a cleanup. */
  onReady?: (context: BabylonContext) => (() => void) | void;
  /** A gizmo drag, already throttled to 10 Hz with a final update on release. */
  onTransformChanged?: (deviceId: string, transform: Transform) => void;
  /** A click on a rig in the viewport, or undefined when the click missed. */
  onSelectCamera?: (deviceId: string | undefined) => void;
  /**
   * Camera rigs and the transform gizmo. The Scene page passes false: a scene is
   * scenery, and a screen that cannot build a rig cannot move one (devplan §4).
   */
  withRigs?: boolean;
}

/**
 * The 3D viewport.
 *
 * React owns the element and nothing inside it: the engine, scene and camera are
 * created once and driven imperatively from here on. Props never flow into the
 * scene graph, so a re-render cannot rebuild it.
 */
export const BabylonCanvas = ({
  className,
  onReady,
  onTransformChanged,
  onSelectCamera,
  withRigs = true,
}: BabylonCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onReadyRef = useRef(onReady);
  const onTransformChangedRef = useRef(onTransformChanged);
  const onSelectCameraRef = useRef(onSelectCamera);
  // Read once, in the mount effect: rigs exist for the life of the canvas or not
  // at all, so a later change would have to rebuild the engine to mean anything.
  const withRigsRef = useRef(withRigs);

  // Kept current out of band so a new callback identity cannot retrigger the
  // mount effect and rebuild the engine.
  useEffect(() => {
    onReadyRef.current = onReady;
    onTransformChangedRef.current = onTransformChanged;
    onSelectCameraRef.current = onSelectCamera;
  }, [onReady, onTransformChanged, onSelectCamera]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    // Babylon renders multi-canvas by drawing into the engine's own canvas and
    // blitting the result to each registered view, so the engine cannot own one
    // that is on screen. The viewport is a view like the preview is.
    const working = document.createElement('canvas');
    const engine = new Engine(working, true, { preserveDrawingBuffer: true, stencil: true }, true);
    const editor = createSceneEditor({ engine, canvas });

    engine.registerView(canvas, editor.camera);
    // Both where pointer coordinates come from and, deliberately, the view
    // Babylon renders last — so the engine ends every frame at viewport size.
    engine.inputElement = canvas;

    // The right button pans, and the camera is attached without preventing
    // defaults, so without this the browser opens its menu mid-drag and takes
    // the release with it. Babylon only clears its "swiping" flag on a release
    // whose button matches, so the flag stays set for the rest of the session
    // and every later click is read as a drag — which silently kills picking,
    // and with it selection in the viewport.
    const swallowMenu = (event: MouseEvent): void => event.preventDefault();
    canvas.addEventListener('contextmenu', swallowMenu);

    const switcher = createSceneSwitcher({ editor });
    const occlusion = createAmbientOcclusion({ scene: editor.scene, camera: editor.camera });
    const rigs = withRigsRef.current
      ? createRigManager({
          editor,
          onTransformChanged: (deviceId, transform) => onTransformChangedRef.current?.(deviceId, transform),
          onSelectRequested: (deviceId) => onSelectCameraRef.current?.(deviceId),
        })
      : undefined;

    // A switch reframes the editor, so rig and gizmo sizes have to follow it.
    const unsubscribe = switcher.subscribe((state) => {
      if (state.status === 'idle') {
        rigs?.refit();
      }
    });

    engine.runRenderLoop(() => editor.scene.render());

    const releaseCaller = onReadyRef.current?.({ editor, switcher, rigs });

    return () => {
      // The caller goes first: it may still need to read the scene on the way out.
      releaseCaller?.();
      unsubscribe();
      canvas.removeEventListener('contextmenu', swallowMenu);
      engine.stopRenderLoop();
      rigs?.dispose();
      occlusion.dispose();
      switcher.dispose();
      editor.dispose();
      engine.dispose();
    };
  }, []);

  return <canvas ref={canvasRef} className={className} />;
};
